import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineStyle,
  createChart,
  type IChartApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useTheme } from '../../hooks/useTheme';
import type { Candle, StructuralZone } from '../../types/market';

interface CandlestickChartProps {
  candles: Candle[];
  zones: StructuralZone[];
}

interface PriceLineHandle {}

interface CandlePoint {
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface VolumePoint {
  time: UTCTimestamp;
  value: number;
  color: string;
}

interface CandleSeriesHandle {
  setData(data: CandlePoint[]): void;
  createPriceLine(options: {
    price: number;
    color: string;
    lineWidth: number;
    lineStyle: number;
    axisLabelVisible: boolean;
    title: string;
  }): PriceLineHandle;
  removePriceLine(line: PriceLineHandle): void;
}

interface VolumeSeriesHandle {
  setData(data: VolumePoint[]): void;
  priceScale(): { applyOptions(options: { scaleMargins: { top: number; bottom: number } }): void };
}

function cssVariable(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function CandlestickChart({ candles, zones }: CandlestickChartProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<CandleSeriesHandle | null>(null);
  const volumeSeriesRef = useRef<VolumeSeriesHandle | null>(null);
  const priceLinesRef = useRef<PriceLineHandle[]>([]);
  const fittedHistoryStartRef = useRef<number | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: cssVariable('--color-surface') },
        textColor: cssVariable('--color-text-muted'),
        attributionLogo: true,
      },
      grid: {
        vertLines: { color: cssVariable('--color-chart-grid') },
        horzLines: { color: cssVariable('--color-chart-grid') },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: cssVariable('--color-chart-crosshair'), labelBackgroundColor: cssVariable('--color-primary') },
        horzLine: { color: cssVariable('--color-chart-crosshair'), labelBackgroundColor: cssVariable('--color-primary') },
      },
      rightPriceScale: {
        borderColor: cssVariable('--color-border'),
        scaleMargins: { top: 0.08, bottom: 0.24 },
      },
      timeScale: {
        borderColor: cssVariable('--color-border'),
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 7,
      },
      handleScroll: true,
      handleScale: true,
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: cssVariable('--color-positive'),
      downColor: cssVariable('--color-negative'),
      wickUpColor: cssVariable('--color-positive'),
      wickDownColor: cssVariable('--color-negative'),
      borderVisible: false,
      priceLineVisible: true,
    });
    candleSeriesRef.current = candleSeries as unknown as CandleSeriesHandle;

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: 'volume',
      priceFormat: { type: 'volume' },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const typedVolumeSeries = volumeSeries as unknown as VolumeSeriesHandle;
    typedVolumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    volumeSeriesRef.current = typedVolumeSeries;

    const observer = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      priceLinesRef.current = [];
      fittedHistoryStartRef.current = null;
    };
  }, [theme]);

  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!candleSeries || !volumeSeries) return;

    candleSeries.setData(
      candles.map((candle) => ({
        time: Math.floor(candle.openTime / 1_000) as UTCTimestamp,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      })),
    );
    volumeSeries.setData(
      candles.map((candle) => ({
        time: Math.floor(candle.openTime / 1_000) as UTCTimestamp,
        value: candle.volume,
        color: candle.close >= candle.open ? cssVariable('--color-chart-volume') : cssVariable('--color-negative-soft'),
      })),
    );

    const historyStart = candles[0]?.openTime ?? null;
    if (historyStart !== null && historyStart !== fittedHistoryStartRef.current && chartRef.current) {
      chartRef.current.timeScale().fitContent();
      fittedHistoryStartRef.current = historyStart;
    }
  }, [candles, theme]);

  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;

    for (const line of priceLinesRef.current) candleSeries.removePriceLine(line);
    priceLinesRef.current = zones.slice(0, 5).map((zone) => {
      const price = (zone.low + zone.high) / 2;
      return candleSeries.createPriceLine({
        price,
        color: zone.kind === 'support' ? cssVariable('--color-positive') : cssVariable('--color-negative'),
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `${zone.kind === 'support' ? 'S' : 'R'} ${zone.strength}`,
      });
    });
  }, [zones, theme]);

  return <div ref={containerRef} className="chart-container" aria-label="Interactive candlestick chart" />;
}
